import React, { useRef, useState } from "react"
import {
  convertToCSV,
  downloadCSV,
  getContextMenuItemsForIndividualReport,
} from "../../../../utils/report.utils"
import { getIndividualTasksOfClientIdLazy } from "../../../../services/report.service"
import useToast from "../../../../hooks/useToast.hook"
import useReportFiltersContext from "../../../../hooks/contextConsumers/useReportFiltersContext.hook"
import ContextMenu from "../../../../components/atoms/ContextMenu.atom"
import Card from "../../../../components/atoms/Card.atom"
import ClientTasksColumnContent from "./ClientTasksColumnContent.controller"
import { useNavigate } from "react-router-dom"
import ModalFormEditTask from "../../../../components/compounds/ModalFormEditTask.compound"
import StatusChip from "../../../../components/molecules/StatusChip.molecule"
import { CLIENT_INDIVIDUAL_REPORT_CSV_HEADERS } from "../../../../consts/reports.consts"
import { DateTime } from "luxon"
import { UNIFIEDDATE_VALUES } from "../../../../consts/CompoundRangeDatePicker"
import {
  CLIENT_SPRINT_STATES,
  CLOSE_TYPE_LABELS,
  CLOSE_TYPES,
} from "../../../../consts/modules/clientReport.consts"
import SprintApprovalTemplate from "../../components/SprintApprovalTemplate.molecule"
import SplitButton from "../../../../components/atoms/SplitButton.atom"
import ModalDownloadPeriodTasks from "./ModalDownloadPeriodTasks.controller"
import { clsx } from "../../../../utils/strings.utils"

export const DEFAULT_SORT_CONFIG = {
  sortField: null,
  sortOrder: null,
}

export const DEFAULT_FILTER_CONFIG = {
  projectCode: [],
  projectName: [],
  categoryId: [],
  specialCategoryId: [],
  moduleId: [],
  taskName: null,
  teamMemberNameTag: [],
}

const RegisteredClientTaskTable = ({
  periodType,
  selectedPeriod,
  clientSprints,
  selectedPeriodObject,
  clientHasValidSprint,
}) => {
  const selectedPeriodNumber = selectedPeriodObject.sprintNumber
  const isTimeOut = selectedPeriodObject.closeType === CLOSE_TYPES.TIMEOUT

  const [isDownloading, setIsDownloading] = useState(false)
  const [updatingTask, setUpdatingTask] = useState(null)
  const [selectedTask, setSelectedTask] = useState(null)
  const [editTaskVisible, setEditTaskVisible] = useState(false)
  const navigate = useNavigate()
  const [isDownloadPeriodTasksVisible, setIsDownloadPeriodTasksVisible] =
    useState(false)

  const tableRef = useRef(null)
  const [rowCount, setRowCount] = useState(0)

  const cmItems = [
    {
      label: "Editar",
      icon: "pi pi-pencil",
      command: async () => {
        setEditTaskVisible(true)
      },
    },
    {
      label: "Historial de cambios",
      icon: "pi pi-history",
      command: () => {
        navigate("/historial-tarea", {
          state: { taskId: selectedTask?.taskId },
        })
      },
    },
  ]

  const { clientReportSelectedClientCode } = useReportFiltersContext()
  const { setErrorMessage } = useToast()

  const cmRef = useRef(null)

  const downloadedToCsv = (data, csvName = "temp_missing_name") => {
    const tasks = data.tasks
      .map((task) => ({
        ...task,
        date: DateTime.fromISO(task?.date?.split("T")[0])
          .set({ hour: 12 })
          .toISODate(),
      }))
      .filter((task) => !task.isSubheader)
    const headers = CLIENT_INDIVIDUAL_REPORT_CSV_HEADERS

    const tasksCSV = convertToCSV(tasks, headers)
    downloadCSV(tasksCSV, csvName)
  }

  const downloadCurrentTableData = async () => {
    if (!clientHasValidSprint) return
    const [startDate, endDate] = selectedPeriod
    setIsDownloading(true)

    const response = await getIndividualTasksOfClientIdLazy(
      clientReportSelectedClientCode,
      tableRef?.current.getFilterValues(),
      tableRef?.current.getSortingValue(),
      startDate,
      endDate,
      0,
      Number.MAX_SAFE_INTEGER
    ).finally(() => setIsDownloading(false))

    if (!response.success) {
      setErrorMessage({ message: response.message })
      return
    }
    const result = response.result

    let csvName = ""
    if (periodType === UNIFIEDDATE_VALUES.SPRINT) {
      const date = DateTime.fromISO(selectedPeriodObject.startDate)
      csvName = `REP_CLI_${clientReportSelectedClientCode}_TareasRegistradas_sprint_${selectedPeriodObject.sprintNumber}-${date.year}`
    } else if (periodType === UNIFIEDDATE_VALUES.SINGLEWEEK) {
      const date = DateTime.fromISO(selectedPeriodObject.startDate)
      csvName = `REP_CLI_${clientReportSelectedClientCode}_TareasRegistradas_semana_${date.weekNumber}-${date.year}`
    } else if (periodType === UNIFIEDDATE_VALUES.SINGLEMONTH) {
      const date = DateTime.fromISO(selectedPeriodObject.startDate)
      csvName = `REP_CLI_${clientReportSelectedClientCode}_TareasRegistradas_mes_${date.monthLong}-${date.year}`
    }

    downloadedToCsv(result, csvName)
  }

  const downloadRangeTasks = async (startDate, endDate) => {
    const response = await getIndividualTasksOfClientIdLazy(
      clientReportSelectedClientCode,
      DEFAULT_FILTER_CONFIG,
      DEFAULT_SORT_CONFIG,
      startDate,
      endDate,
      0,
      Number.MAX_SAFE_INTEGER
    )

    if (!response.success) {
      setErrorMessage({ message: response.message })
      return
    }
    const result = response.result

    const csvName = `REP_CLI_${clientReportSelectedClientCode}_TareasRegistradas_${startDate.toFormat("dd-MM-yyyy")}_${endDate.toFormat("dd-MM-yyyy")}`
    downloadedToCsv(result, csvName)
  }

  const downloadAllTasks = async () => {
    setIsDownloading(true)
    const response = await getIndividualTasksOfClientIdLazy(
      clientReportSelectedClientCode,
      DEFAULT_FILTER_CONFIG,
      DEFAULT_SORT_CONFIG,
      DateTime.now().minus({ years: 40 }).toISO(),
      DateTime.now().plus({ years: 1 }).toISO(),
      0,
      Number.MAX_SAFE_INTEGER
    )
    setIsDownloading(false)
    if (!response.success) {
      setErrorMessage({ message: response.message })
      return
    }
    const result = response.result
    const totalsByDayAllDates = Object.keys(result.totalsByDay).map((date) =>
      DateTime.fromISO(date)
    )
    const earliestRecordedDate = totalsByDayAllDates.sort(
      (a, b) => a.toMillis() - b.toMillis()
    )[0]
    const latestRecordedDate = totalsByDayAllDates.sort(
      (a, b) => b.toMillis() - a.toMillis()
    )[0]

    const csvName = `REP_CLI_${clientReportSelectedClientCode}_TareasRegistradas_${earliestRecordedDate.toFormat("dd-MM-yyyy")}_${latestRecordedDate.toFormat("dd-MM-yyyy")}`

    downloadedToCsv(result, csvName)
  }

  return (
    <Card>
      <div className="flex justify-content-between">
        <div className="flex align-items-center gap-2">
          <p className="text-xl mb-0 font-cairo">
            Tareas de
            {periodType === UNIFIEDDATE_VALUES.SPRINT &&
              `l sprint ${(isTimeOut ? CLOSE_TYPE_LABELS.TIMEOUT.toLowerCase() : selectedPeriodNumber) || ""}`}
            {periodType === UNIFIEDDATE_VALUES.SINGLEWEEK && " la semana"}
            {periodType === UNIFIEDDATE_VALUES.SINGLEMONTH && " el mes"}
          </p>
          {periodType !== UNIFIEDDATE_VALUES.SPRINT && (
            <StatusChip
              status={selectedPeriodObject.state}
              tooltipContent={
                <SprintApprovalTemplate
                  date={selectedPeriodObject?.approvalDate}
                  approverFullName={`${
                    selectedPeriodObject?.approvedByName
                  } ${selectedPeriodObject?.approvedByLastName}`}
                />
              }
            />
          )}
          {periodType === UNIFIEDDATE_VALUES.SPRINT && (
            <StatusChip
              status={
                clientSprints.find(
                  (s) => s.sprintNumber === selectedPeriodNumber
                )?.state
              }
              tooltip={
                clientSprints.find(
                  (s) => s.sprintNumber === selectedPeriodNumber
                )?.state === CLIENT_SPRINT_STATES.APPROVED
              }
              tooltipContent={
                <SprintApprovalTemplate
                  date={selectedPeriodObject?.approvalDate}
                  approverFullName={`${
                    selectedPeriodObject?.approvedByName
                  } ${selectedPeriodObject?.approvedByLastName}`}
                />
              }
            />
          )}
        </div>
        <SplitButton
          label="Descargar"
          icon={clsx(
            "pi",
            isDownloading ? "pi-spinner pi-spin" : "pi-download"
          )}
          className="h-fit mr-3"
          disabled={
            !clientHasValidSprint ||
            rowCount === 0 ||
            isDownloading ||
            (periodType === UNIFIEDDATE_VALUES.SPRINT &&
              clientSprints.find((s) => s.sprintNumber === selectedPeriodNumber)
                ?.state === CLIENT_SPRINT_STATES.IN_PROGRESS)
          }
          onClick={downloadCurrentTableData}
          model={[
            {
              label: "Todos los registros",
              command: downloadAllTasks,
            },
            {
              label: "Por rango personalizado",
              command: () => {
                setIsDownloadPeriodTasksVisible(true)
              },
            },
          ]}
        />
      </div>

      <ClientTasksColumnContent
        ref={tableRef}
        contextMenuRef={cmRef}
        setContextMenuTarget={setSelectedTask}
        updatingTask={updatingTask}
        selectedPeriod={selectedPeriod}
        onRowCountChange={setRowCount}
        clientHasValidSprint={clientHasValidSprint}
        periodType={periodType}
      />
      <ContextMenu
        ref={cmRef}
        model={getContextMenuItemsForIndividualReport(selectedTask, cmItems)}
      />
      <ModalFormEditTask
        visible={editTaskVisible}
        setVisible={setEditTaskVisible}
        task={selectedTask}
        teamMemberId={selectedTask?.teamMemberId}
        onSubmit={() => {
          setUpdatingTask(selectedTask)
        }}
        onEditCompleted={(errorMessage) => {
          if (errorMessage) {
            setErrorMessage({ message: errorMessage })
          }
          setEditTaskVisible(false)
          setUpdatingTask(null)
          tableRef.current.refreshData()
        }}
        onlyActiveProjects={false}
        isAdminEdit
        waitForResponse={false}
      />
      <ModalDownloadPeriodTasks
        visible={isDownloadPeriodTasksVisible}
        setVisible={setIsDownloadPeriodTasksVisible}
        downloadRangeTasks={downloadRangeTasks}
      />
    </Card>
  )
}

export default RegisteredClientTaskTable
